import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentService } from '../payment.service';
import { Settlement } from '../payment.type';
import { SupplierFilter } from '../../shared/components/supplier-filter/supplier-filter';
import { MAX_PROOFS, PROOF_ACCEPT, addProofFiles } from '../proof-files';

/**
 * Records a payment that is not for any transfer run or contract — a bonus, or
 * the fix for a mistake — so it is kept in the app and shows up in the
 * accountant's report. Like every settlement it is recorded as pending and
 * marked paid once the transfer has actually been sent.
 */
@Component({
  selector: 'app-manual-settlement-dialog',
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatSelectModule, SupplierFilter,
  ],
  templateUrl: './manual-settlement-dialog.html',
  styleUrl: './manual-settlement-dialog.scss',
})
export class ManualSettlementDialog {
  private paymentService = inject(PaymentService);
  private snackBar = inject(MatSnackBar);
  readonly dialogRef = inject<MatDialogRef<ManualSettlementDialog, Settlement | null>>(MatDialogRef);

  readonly maxNoteLength = 1000;
  readonly maxProofs = MAX_PROOFS;
  readonly accept = PROOF_ACCEPT;

  supplierId = '';
  kind: 'Bonus' | 'Correction' = 'Bonus';
  amount: number | null = null;
  note = '';
  proofFiles: File[] = [];
  fileError = '';
  saving = false;

  get valid(): boolean {
    return !!this.supplierId && !!this.amount && this.amount > 0 && Number.isInteger(this.amount)
      && this.note.trim().length > 0;
  }

  onFilesSelected(event: Event): void {
    const { files, error } = addProofFiles(event.target as HTMLInputElement, this.proofFiles);
    this.proofFiles = files;
    this.fileError = error;
  }

  removeFile(index: number): void {
    this.proofFiles = this.proofFiles.filter((_, i) => i !== index);
    this.fileError = '';
  }

  submit(): void {
    if (!this.valid || this.saving) return;
    this.saving = true;
    this.paymentService.createManualSettlement({
      supplier_id: this.supplierId,
      kind: this.kind,
      amount: this.amount!,
      note: this.note.trim(),
    }, this.proofFiles).subscribe({
      next: s => {
        this.snackBar.open('Recorded — mark it paid once the transfer is sent.', 'Close', { duration: 6000 });
        this.dialogRef.close(s);
      },
      error: err => {
        this.saving = false;
        this.snackBar.open(err?.error?.error ?? 'Failed to record the payment', 'Close', { duration: 6000 });
      },
    });
  }
}
